<template>
  <div class="main" :style="fullscreen.style">
    <studio-logo id="studio-logo"/>
    <div class="background" v-if="!isMobile" />
    <header-banner />

    <!-- We used this as anchor point for social media appear event -->
    <div data-aos="appear" data-aos-id="social" data-aos-anchor-placement="center-center"></div>
    <div class="content">
      <picture-text-header title="Overview" />
      <text-typer class="header-text" :text="`
      Project Synthecite is a 2D pixel art atmospheric sci-fi horror game with a focus on narrative, puzzles, and survival.
      Using a unique blend of retro-futuristic and cyberpunk aesthetics, Project Synthecite aims to deliver a unique and immersive experience that will leave you questioning your own sanity.
      Use your AI companion to intelligently inform you about the world, as well as utilize a unique set of hacking tools to control the ship's vast network of devices and technology.
      `" :charWrite="5"/>
      <text-typer class="header-text" :text="`
      Set in the distant future where humanity has reached a boiling point of conflict and desperately requires the need to expand its intergalactic borders
      You play as a member of a group of scientists set out on a mission into the deep reaches of space to find a new home for humanity, and in search of Synthecite, a newly discovered material that could change the course of humanity forever. 
      As you awake from your cryogenic sleep, you find yourself alone in a dark and desolate ship with no recollection of the past. The remains of your lacerated crew members strewn onboard quickly reveal that something has gone terribly wrong.
      As you delve deeper into this hellish nightmare, you will discover that you are not alone, and that the ship is not the only thing that is decaying.

      With only your Personal Assistant Lexicon (P.A.L), an AI companion to guide you and monitor your mental health, you must explore the ship and uncover the truth behind the events that have transpired.      
      Use P.A.L's to uncover the truth by interfacing with the vast network of devices and systems onboard the ship, and use your wits to survive the horrors that await you.
      `" :charWrite="5"/>
      <text-typer class="header-text disclaimer" :text="`
      [WARNING] This project is currently on hold indefinitely. If you are interested in its revival, please contact us: m.bitzos@gmail.com
      `" :charWrite="5"/>
    </div>
    <div class="content">
      <picture-text
        v-for="(pic,i) in pictureTexts"
        :key="i"
        :header="pic.header"
        :description="pic.description"
        :imageSrc="pic.image.url"
        :imageAlt="pic.image.alt"
        :glitchImageSrc="pic.image.glitch"
        :left="i % 2 == 0"
      />
    </div>
    <picture-carousel :pictures="carouselPictures" class="carousel" />

    <div class="content subscribe">
      <subscribe />
    </div>
    <footerr />

    <!-- Social Media -->
    <!-- Must be at the bottom to prevent weird overlay bug -->
    <transition name="fade">
      <div v-if="(isMobile || socialMediaVisible) && !fullscreen.enabled">
        <div class="social-media-desktop">
          <social-media />
        </div>
        <div class="social-media-mobile">
          <social-media-mobile />
        </div>
      </div>
    </transition>
    <crt-overlay />
    <image-fullscreen-viewer />
  </div>
</template>

<script>
import "@/views/project-synthecite/styles/scrollbar.scss"
// Scripts
import HeaderBanner from "./header"
import Footerr from "./footer"
import PictureText from "./components/picture-text"
import SocialMedia from "./social-media/social-media-bar"
import SocialMediaMobile from "./social-media/social-media-mobile-bar"
import PictureCarousel from "./picture-carousel"
import ImageFullscreenViewer from "./image-fullscreen-viewer"
import Subscribe from "./subscribe/"
import TextTyper from "./components/text-typer"
import PictureTextHeader from "./components/picture-text-header"
import CrtOverlay from './crt-overlay'

import StudioLogo from "@/components/studio-logo.vue"

// Scripts
import GlitchController from "@/scripts/glitch-controller.js"
import { mapState } from "vuex"

export default {
  components: {
    HeaderBanner,
    PictureText,
    Footerr,
    SocialMedia,
    SocialMediaMobile,
    PictureCarousel,
    ImageFullscreenViewer,
    CrtOverlay,
    Subscribe,
    TextTyper,
    PictureTextHeader,
    StudioLogo,
  },
  computed: {
    ...mapState({
      fullscreen: state => state.projectSynthecite.fullscreen,
      isMobile: state => state.isMobile,
    })
  },
  data() {
    return {
      socialMediaVisible: false,
      headerText: `P`,
      pictureTexts: [
        {
          header: "Explore a ship in the middle of deep space",
          description:
            "After waking from an extended stasis and battling amnesia, the ship you once called home has become a dark and twisted maze for you to explore.",
          image: {
            url: "showcase/hallways.gif",
            alt: "ship exploration"
          }
        },
        {
          header: "Meet P.A.L",
          description: "Equipped with a handheld tablet, each member of the mission has access to their Personal Assistant Lexicon, or P.A.L for short. Converse with an AI that will guide you, as well as act as a tool to interface with your surroundings.",
          image: {
            url: "showcase/pal-conversation.gif",
            alt: "PAL conversing"
          }
        },
        {
          header: "Traverse the file system",
          description:"As you explore the ship you have access to a digital network for you to uncover virtually. Hidden secrets of the past and future, as well as configurations that with a bit of tinkering, can let you control the ship's systems.",
          image: {
            url: "showcase/pal-file-system.gif",
            alt: "PAL file system"
          }
        },
        {
          header: "Use your wits to solve puzzles",
          description: "Use all tools in your arsenal around you to solve puzzles and progress through the ship. You'll find that technology is as much a friend as it is a foe.",
          image: {
            url: "showcase/pal-incinerator.gif",
            alt: "PAL incinerator example"
          }
        },
        {
          header: "Hack devices, control the ship",
          description: "As all devices aboard the ship are powered on a virtual network, use your hacking tools to control how the ship operates.",
          image: {
            url: "showcase/devices.gif",
            alt: "Hacking example"
          }
        },
        {
          header: "You are not alone",
          description: "Sneak your way around the twisted, hellish threats aboard this ship. You must use your survival instincts to avoid the horrors that await you",
          image: {
            url: "showcase/enemy-sneak1.gif",
            alt: "Enemy example"
          }
        },
      ],
      carouselPictures: [
        "showcase/carousel/dragger-swarm.png",
        "showcase/carousel/CONCEPT-stasis-room.jpg",
        "showcase/carousel/enemy-attack.gif",
        "showcase/carousel/demo_stasis_v0.gif",
        "showcase/carousel/CONCEPT-configfile.jpg",
        "showcase/carousel/filesystem.png",
        "showcase/carousel/incinerator.png",
        "showcase/carousel/CONCEPT-enemy_prototype_left_with_backgrounds_and_light_shake.gif",
        "showcase/carousel/body.png",
        "showcase/carousel/pal-console-talk.png",
        "showcase/carousel/CONCEPT-hackktool.gif",
        "showcase/carousel/CONCEPT-mapscreen.jpg",
      ]
    }
  },
  mounted() {
    // mobile shouldnt have glitch as its too disorienting
    if (!this.isMobile) {
      GlitchController.Start()
    }
  },
  created() {
    // handles showing the social media bar based on scroll
    document.addEventListener("aos:in:social", this.showSocialMedia)
    document.addEventListener("aos:out:social", this.unshowSocialMedia)
  },
  beforeDestroy() {
    // handles showing the social media bar based on scroll
    document.removeEventListener("aos:in:social", this.showSocialMedia)
    document.removeEventListener("aos:out:social", this.unshowSocialMedia)
    GlitchController.Stop()
  },
  methods: {
    /**
     * Shows the social media bar
     */
    showSocialMedia() {
      this.socialMediaVisible = true
    },
    /**
     * Shows the social media bar
     */
    unshowSocialMedia() {
      this.socialMediaVisible = false
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/views/project-synthecite/styles/variables.scss";

.main {
  /* For full screen offset */
  position: relative;

  // this should never happen
  overflow-x: hidden;

  font-family: "VT323", monospace;
  @import "@/views/project-synthecite/styles/scrollbar.scss";
}
.background {
  position: fixed;
  z-index: -10;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  background-color: black;
}
.background::after {
  content: "";
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba($green, 0.03) 40%, black 100%);
  animation: 10s background-glow ease infinite;
}

@keyframes background-glow {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.5;
  }
}
.content {
  width: 60%;
  margin: auto;
}
@media only screen and (max-width: 1300px) {
  .content {
    width: 80%;
  }
}
@media only screen and (max-width: 800px) {
  .content {
    width: 90%;
  }
}
.social-media-mobile {
  display: none;
}
.social-media-desktop {
  display: initial;
}
@media only screen and (max-width: 900px) {
  .social-media-desktop {
    display: none;
  }
  .social-media-mobile {
    display: initial;
  }
}
.carousel {
  width: 100%;
  height: 340px;
  margin-top: 150px;
  margin-bottom: 100px;
}
.fade-enter-active {
  transition: 0.5s all;
}
.fade-leave-active {
  transition: 0.5s all;
}
.fade-leave,
.fade-enter {
  opacity: 1;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/**
Even though this isnt doing anything
It triggers the event which is needed
 */
[data-aos="appear"] {
  opacity: 0;
  transition: 1s opacity;
  &.aos-animate {
    opacity: 1;
  }
}

.header-text {
  color: $text-color;
  font-size:  max(20px, min(25px, 4vw));
  margin-top: 50px;

  &.disclaimer {
    // font-size: 25px;
    color: $warning-text-color;
  }
}
.subscribe {
  padding-top: 50px;
}

#studio-logo {
  position: fixed;
  left: 10px;
  top: 10px;
  z-index: 1000;
}
</style>

